import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  combineLatest,
  distinctUntilChanged,
  exhaustMap,
  filter,
  fromEvent,
  map,
  merge,
  skip,
  startWith,
  switchMap,
  timer,
} from 'rxjs';
import { runtimeEnv } from '@/lib/runtimeEnv';

/** The bars: how strong is the user's connection to Lureo's systems. */
export const SignalStrength = {
  None: 'none',
  Low: 'low',
  Medium: 'medium',
  Strong: 'strong',
} as const;

export type SignalStrength = (typeof SignalStrength)[keyof typeof SignalStrength];

/** Notable happenings for toasts/log — always aligned with the bars because both derive from the same banded axes. */
export const ServiceEvent = {
  Offline: 'offline',
  BackOnline: 'backOnline',
  ServiceUnavailable: 'serviceUnavailable',
  ServiceRestored: 'serviceRestored',
} as const;

export type ServiceEvent = (typeof ServiceEvent)[keyof typeof ServiceEvent];

/** Layer identity for the instant status-change stream. */
export const ServiceLayer = {
  Network: 'network',
  Service: 'service',
} as const;

export type ServiceLayer = (typeof ServiceLayer)[keyof typeof ServiceLayer];

export type ServiceStatusChange = { layer: ServiceLayer; strength: SignalStrength };

/** Point-in-time bands and probe windows (null = failed probe), for telemetry. */
export type ServiceSnapshot = {
  network: SignalStrength;
  service: SignalStrength;
  networkSamplesMs: (number | null)[];
  serviceSamplesMs: (number | null)[];
};

const BACKEND_URL = (runtimeEnv.VITE_BACKEND_URL || process.env.VITE_BACKEND_URL || '') as string;
// The backend's liveness check, outside /api/v1 by design (Fly polls it): no credentials, no 401s in its logs
const SERVICE_PROBE_PATH = '/health';

const PROBE_INTERVAL_MS = 5000;
const PROBE_TIMEOUT_MS = 4000; // must stay below the interval so probe rounds never overlap
const ROLLING_WINDOW_SIZE = 6;
// an axis holds its value until it has this much evidence — one slow or failed
// probe on a cold window must never own the verdict
const MIN_SAMPLES_TO_BAND = 3;
const STRONG_MAX_RTT_MS = 150;
const MEDIUM_MAX_RTT_MS = 400;
// Slow is not down: an answered probe bands Low at worst, only failed probes reach None
const FAILURES_FOR_NONE = 3;
const EVENT_HOLD_MS = 5000; // a state must hold this long before it becomes an event

const STRENGTH_RANK: Record<SignalStrength, number> = {
  [SignalStrength.None]: 0,
  [SignalStrength.Low]: 1,
  [SignalStrength.Medium]: 2,
  [SignalStrength.Strong]: 3,
};

type ProbeSample = number | null;

function bandOfRtt(rttMs: number): SignalStrength {
  if (rttMs < STRONG_MAX_RTT_MS) return SignalStrength.Strong;
  if (rttMs < MEDIUM_MAX_RTT_MS) return SignalStrength.Medium;
  return SignalStrength.Low;
}

export function bandOfWindow(window: ProbeSample[]): SignalStrength {
  const answered = window.filter((sample): sample is number => sample !== null);
  if (answered.length === 0 || window.length - answered.length >= FAILURES_FOR_NONE) return SignalStrength.None;
  return bandOfRtt(median(answered));
}

function weakest(bands: SignalStrength[]): SignalStrength {
  return bands.reduce((worst, band) => (STRENGTH_RANK[band] < STRENGTH_RANK[worst] ? band : worst));
}

function median(window: number[]): number {
  const sorted = [...window].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

export class ServiceMonitor {
  // The two axes are the single source of truth: a rolling probe window banded to a strength.
  // Only our own probes feed them (the browser's online flag is never trusted); bars AND events
  // both read from these, so an event can never disagree with the bars.
  private readonly networkAxis = new BehaviorSubject<SignalStrength>(SignalStrength.Strong);
  private readonly serviceAxis = new BehaviorSubject<SignalStrength>(SignalStrength.Strong);

  private readonly barsSubject = new BehaviorSubject<SignalStrength>(SignalStrength.Strong);
  private readonly eventsSubject = new Subject<ServiceEvent>();
  private readonly disruptionSubject = new BehaviorSubject<boolean>(false);

  private readonly networkWindow: ProbeSample[] = [];
  private readonly serviceWindow: ProbeSample[] = [];

  private subscriptions: Subscription | null = null;

  /** The bars — deduplicated, weakest axis wins. */
  readonly signalStrength$: Observable<SignalStrength> = this.barsSubject.pipe(distinctUntilChanged());

  /** Debounced notable transitions, cause included; derived from the same axes as the bars. */
  readonly events$: Observable<ServiceEvent> = this.eventsSubject.asObservable();

  /** Instant per-layer bands, undebounced — for breadcrumbs, telemetry, and UI state machines. */
  readonly networkStrength$: Observable<SignalStrength> = this.networkAxis.pipe(distinctUntilChanged());
  readonly serviceStrength$: Observable<SignalStrength> = this.serviceAxis.pipe(distinctUntilChanged());

  /** The moment any layer's band moves — instant, cause included. */
  readonly statusChanged$: Observable<ServiceStatusChange> = merge(
    this.networkAxis.pipe(distinctUntilChanged(), skip(1), map(strength => ({ layer: ServiceLayer.Network, strength }))),
    this.serviceAxis.pipe(distinctUntilChanged(), skip(1), map(strength => ({ layer: ServiceLayer.Service, strength })))
  );

  /**
   * Banner feed: true while the backend is down, gated on the user's own internet
   * being up, held so it can't flicker. Late subscribers get the current value.
   */
  readonly serviceDisruption$: Observable<boolean> = this.disruptionSubject.pipe(distinctUntilChanged());

  get current(): SignalStrength {
    return this.barsSubject.value;
  }

  snapshot(): ServiceSnapshot {
    return {
      network: this.networkAxis.value,
      service: this.serviceAxis.value,
      networkSamplesMs: [...this.networkWindow],
      serviceSamplesMs: [...this.serviceWindow],
    };
  }

  start() {
    if (this.subscriptions) return;
    const subscriptions = new Subscription();

    const visible$ = fromEvent(document, 'visibilitychange').pipe(
      filter(() => document.visibilityState === 'visible')
    );
    const online$ = fromEvent(window, 'online');
    // The browser saying offline only prompts a round: it can say so on a connection that works
    const offline$ = fromEvent(window, 'offline');

    // One tick samples every axis at once; exhaustMap ignores ticks while a round is in
    // flight. Background tabs skip; online/offline/visible fire an immediate round.
    const tick$ = merge(timer(0, PROBE_INTERVAL_MS), online$, offline$, visible$).pipe(
      filter(() => !document.hidden)
    );
    subscriptions.add(tick$.pipe(exhaustMap(() => this.sampleAllAxes())).subscribe());

    // Banner feed: the backend down while the user's own internet is fine.
    subscriptions.add(
      combineLatest([this.serviceAxis, this.networkAxis])
        .pipe(
          map(([service, network]) => network !== SignalStrength.None && service === SignalStrength.None),
          distinctUntilChanged(),
          switchMap(down => timer(EVENT_HOLD_MS).pipe(map(() => down))), // same hold as events: no flicker
          distinctUntilChanged()
        )
        .subscribe(down => this.disruptionSubject.next(down))
    );

    // Bars = weakest layer.
    subscriptions.add(
      combineLatest([this.networkAxis, this.serviceAxis])
        .pipe(map(weakest), distinctUntilChanged())
        .subscribe(bars => this.barsSubject.next(bars))
    );

    // Events = held transitions of the banded axes. Service "down" is gated on the network
    // axis so we never blame Lureo while the user's own internet is gone; the gate
    // re-evaluates when the network recovers, so a still-down axis then gets its event.
    subscriptions.add(
      merge(
        this.axisEvents(this.networkAxis, ServiceEvent.Offline, ServiceEvent.BackOnline, false),
        this.axisEvents(this.serviceAxis, ServiceEvent.ServiceUnavailable, ServiceEvent.ServiceRestored, true)
      ).subscribe(event => this.eventsSubject.next(event))
    );

    this.subscriptions = subscriptions;
  }

  stop() {
    this.subscriptions?.unsubscribe();
    this.subscriptions = null;
  }

  private axisEvents(
    axis$: Observable<SignalStrength>,
    down: ServiceEvent,
    up: ServiceEvent,
    gatedByNetwork: boolean
  ): Observable<ServiceEvent> {
    const down$ = gatedByNetwork
      ? combineLatest([axis$, this.networkAxis]).pipe(
          map(([band, network]) => band === SignalStrength.None && network !== SignalStrength.None)
        )
      : axis$.pipe(map(band => band === SignalStrength.None));

    return down$.pipe(
      distinctUntilChanged(),
      switchMap(isDown => timer(EVENT_HOLD_MS).pipe(map(() => isDown))), // must hold to be news
      startWith(false),
      distinctUntilChanged(),
      skip(1), // never emit for the initial healthy state
      map(isDown => (isDown ? down : up))
    );
  }

  private async sampleAllAxes(): Promise<void> {
    // The browser's online flag is never read: it can say offline on a connection that works. Our own round trips
    // decide, through the rolling windows.
    const [siteRttMs, serviceRttMs] = await Promise.all([
      this.measureHttpRtt(`${window.location.origin}/favicon.ico`),
      // Opaque (no-cors): the probe needs no readable answer, only one that arrives, and sends no preflight
      this.measureHttpRtt(`${BACKEND_URL}${SERVICE_PROBE_PATH}`, 'no-cors'),
    ]);
    this.recordNetwork(siteRttMs);
    // Neither answered: that is the user's internet, not a Lureo outage, and must not poison the service window
    if (siteRttMs !== null || serviceRttMs !== null) this.recordService(serviceRttMs);
  }

  private recordNetwork(rttMs: number | null) {
    // Asymmetric recovery: a hard None lifts on the first proven round-trip instead of
    // waiting for the failures to roll out of the window. Down stays debounced.
    if (rttMs !== null && this.networkAxis.value === SignalStrength.None) this.networkWindow.length = 0;
    this.pushSample(this.networkWindow, rttMs);
    if (this.networkWindow.length >= MIN_SAMPLES_TO_BAND) {
      this.networkAxis.next(bandOfWindow(this.networkWindow));
    } else if (rttMs !== null && this.networkAxis.value === SignalStrength.None) {
      this.networkAxis.next(bandOfRtt(rttMs));
    }
  }

  private recordService(rttMs: number | null) {
    this.pushSample(this.serviceWindow, rttMs);
    if (this.serviceWindow.length >= MIN_SAMPLES_TO_BAND) {
      this.serviceAxis.next(bandOfWindow(this.serviceWindow));
    } else if (rttMs !== null && this.serviceAxis.value === SignalStrength.None) {
      this.serviceAxis.next(bandOfRtt(rttMs));
    }
  }

  private pushSample(window: ProbeSample[], rttMs: number | null) {
    window.push(rttMs === null ? null : Math.round(rttMs));
    if (window.length > ROLLING_WINDOW_SIZE) window.shift();
  }

  /** Round-trip time in ms, or null when the target never answered (transport failure/timeout). */
  private async measureHttpRtt(url: string, mode: RequestMode = 'cors'): Promise<number | null> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    const startedAt = performance.now();
    try {
      // Any response proves the round-trip; status doesn't matter (a 403 is still a live server).
      // GET, not HEAD: the backend rejects HEAD with 405 (/health allows GET only).
      const target = new URL(url);
      target.searchParams.set('cb', String(Date.now()));
      await fetch(target, {
        method: 'GET',
        mode,
        cache: 'no-store',
        signal: controller.signal,
      });
      return Math.min(performance.now() - startedAt, PROBE_TIMEOUT_MS);
    } catch {
      return null;
    } finally {
      clearTimeout(timeout);
    }
  }
}

export const serviceMonitor = new ServiceMonitor();
